import { ConfirmModal } from '@/components/admin/confirm-modal';
import { DataTable } from '@/components/admin/data-table';
import { type AdminInterest, admin } from '@/lib/admin-api';
import { interests as interestsApi } from '@/lib/api';
import { Database, Edit, Merge, Plus, Trash } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

export default function AdminInterestsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [interests, setInterests] = useState<AdminInterest[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const page = Number(searchParams.get('page')) || 1;

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState<AdminInterest | null>(null);
  const [formData, setFormData] = useState({ name: '', category: '' });
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [seedResult, setSeedResult] = useState<number | null>(null);

  // Sort interests alphabetically by translated name
  const sortedInterests = useMemo(() => {
    return [...interests].sort((a, b) => {
      const nameA = t(`interests.items.${a.name}`, { defaultValue: a.name });
      const nameB = t(`interests.items.${b.name}`, { defaultValue: b.name });
      return nameA.localeCompare(nameB);
    });
  }, [interests, t]);

  const fetchInterests = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await admin.interests.list({ page, limit: 50 });
      setInterests(result.interests);
      setPagination({
        page: result.pagination.page,
        totalPages: result.pagination.totalPages,
        total: result.pagination.total
      });
    } catch (err) {
      console.error('Failed to fetch interests:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchInterests();
  }, [fetchInterests]);

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.category.trim()) return;
    setIsProcessing(true);
    setError(null);
    try {
      await admin.interests.create(formData);
      await fetchInterests();
      setShowCreateModal(false);
      setFormData({ name: '', category: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create interest');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedInterest) return;
    setIsProcessing(true);
    setError(null);
    try {
      await admin.interests.update(selectedInterest.id, formData);
      await fetchInterests();
      setShowEditModal(false);
      setSelectedInterest(null);
      setFormData({ name: '', category: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update interest');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMerge = async () => {
    if (!selectedInterest || !mergeTargetId) return;
    setIsProcessing(true);
    setError(null);
    try {
      await admin.interests.merge(selectedInterest.id, mergeTargetId);
      await fetchInterests();
      setShowMergeModal(false);
      setSelectedInterest(null);
      setMergeTargetId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge interests');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInterest) return;
    setIsProcessing(true);
    setError(null);
    try {
      await admin.interests.delete(selectedInterest.id);
      await fetchInterests();
      setShowDeleteModal(false);
      setSelectedInterest(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete interest');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSeed = async () => {
    setIsProcessing(true);
    setError(null);
    setSeedResult(null);
    try {
      const result = await interestsApi.seed();
      setSeedResult(result.seeded);
      await fetchInterests();
      if (result.seeded === 0) {
        setShowSeedModal(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seed interests');
      setShowSeedModal(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('admin.interests.name'),
      render: (interest: AdminInterest) => (
        <span className="font-medium text-ink">
          {t(`interests.items.${interest.name}`, { defaultValue: interest.name })}
        </span>
      )
    },
    {
      key: 'category',
      header: t('admin.interests.category'),
      render: (interest: AdminInterest) => (
        <span className="text-sepia/80">
          {t(`interests.categories.${interest.category}`, { defaultValue: interest.category })}
        </span>
      )
    },
    {
      key: 'users',
      header: t('admin.interests.users'),
      render: (interest: AdminInterest) => (
        <span className="text-sepia/80">{interest._count.users}</span>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (interest: AdminInterest) => (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setSelectedInterest(interest);
              setFormData({ name: interest.name, category: interest.category });
              setShowEditModal(true);
            }}
            className="p-1 text-sepia/50 hover:text-sepia/80"
            title={t('admin.common.edit')}
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setSelectedInterest(interest);
              setShowMergeModal(true);
            }}
            className="p-1 text-sepia/50 hover:text-blue-600"
            title={t('admin.interests.merge')}
          >
            <Merge className="w-4 h-4" />
          </button>
          {interest._count.users === 0 && (
            <button
              type="button"
              onClick={() => {
                setSelectedInterest(interest);
                setShowDeleteModal(true);
              }}
              className="p-1 text-sepia/50 hover:text-red-600"
              title={t('admin.common.delete')}
            >
              <Trash className="w-4 h-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">{t('admin.interests.title')}</h1>
          <p className="text-sepia/70 mt-1">{t('admin.interests.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              setSeedResult(null);
              setShowSeedModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-warm/30 text-sepia rounded-lg hover:bg-gray-warm/50 transition-colors"
          >
            <Database className="w-4 h-4" />
            {t('admin.interests.seed')}
          </button>
          <button
            type="button"
            onClick={() => {
              setFormData({ name: '', category: '' });
              setShowCreateModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue text-white rounded-lg hover:bg-blue/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('admin.interests.create')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      <DataTable
        columns={columns}
        data={sortedInterests}
        keyExtractor={(interest) => interest.id}
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: handlePageChange
        }}
        emptyMessage={t('admin.interests.noInterests')}
        isLoading={isLoading}
      />

      {/* Create Modal */}
      <ConfirmModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormData({ name: '', category: '' });
        }}
        onConfirm={handleCreate}
        title={t('admin.interests.createTitle')}
        description={
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.interests.name')}
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
              />
            </div>
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.interests.category')}
              </label>
              <input
                id="category"
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
              />
            </div>
          </div>
        }
        confirmText={t('admin.common.create')}
        variant="info"
        isLoading={isProcessing}
      />

      {/* Edit Modal */}
      <ConfirmModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedInterest(null);
          setFormData({ name: '', category: '' });
        }}
        onConfirm={handleUpdate}
        title={t('admin.interests.editTitle')}
        description={
          <div className="space-y-4">
            <div>
              <label htmlFor="editName" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.interests.name')}
              </label>
              <input
                id="editName"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
              />
            </div>
            <div>
              <label htmlFor="editCategory" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.interests.category')}
              </label>
              <input
                id="editCategory"
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
              />
            </div>
          </div>
        }
        confirmText={t('admin.common.save')}
        variant="info"
        isLoading={isProcessing}
      />

      {/* Merge Modal */}
      <ConfirmModal
        isOpen={showMergeModal}
        onClose={() => {
          setShowMergeModal(false);
          setSelectedInterest(null);
          setMergeTargetId('');
        }}
        onConfirm={handleMerge}
        title={t('admin.interests.mergeTitle')}
        description={
          <div className="space-y-4">
            <p>
              {t('admin.interests.mergeDescription', {
                name: selectedInterest
                  ? t(`interests.items.${selectedInterest.name}`, {
                      defaultValue: selectedInterest.name
                    })
                  : ''
              })}
            </p>
            <div>
              <label htmlFor="mergeTarget" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.interests.mergeTarget')}
              </label>
              <select
                id="mergeTarget"
                value={mergeTargetId}
                onChange={(e) => setMergeTargetId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
              >
                <option value="">{t('admin.interests.selectTarget')}</option>
                {sortedInterests
                  .filter((i) => i.id !== selectedInterest?.id)
                  .map((interest) => (
                    <option key={interest.id} value={interest.id}>
                      {t(`interests.items.${interest.name}`, { defaultValue: interest.name })} (
                      {t(`interests.categories.${interest.category}`, {
                        defaultValue: interest.category
                      })}
                      )
                    </option>
                  ))}
              </select>
            </div>
          </div>
        }
        confirmText={t('admin.interests.merge')}
        variant="warning"
        isLoading={isProcessing}
      />

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedInterest(null);
        }}
        onConfirm={handleDelete}
        title={t('admin.interests.deleteTitle')}
        description={t('admin.interests.deleteDescription', {
          name: selectedInterest
            ? t(`interests.items.${selectedInterest.name}`, { defaultValue: selectedInterest.name })
            : ''
        })}
        confirmText={t('admin.common.delete')}
        variant="danger"
        isLoading={isProcessing}
      />

      {/* Seed Modal */}
      <ConfirmModal
        isOpen={showSeedModal}
        onClose={() => {
          setShowSeedModal(false);
          setSeedResult(null);
        }}
        onConfirm={
          seedResult !== null
            ? () => {
                setShowSeedModal(false);
                setSeedResult(null);
              }
            : handleSeed
        }
        title={t('admin.interests.seedTitle')}
        description={
          seedResult !== null ? (
            <div className="text-center">
              <p className="text-lg font-medium text-green-700">
                {t('admin.interests.seedSuccess', { count: seedResult })}
              </p>
            </div>
          ) : (
            <p>{t('admin.interests.seedDescription')}</p>
          )
        }
        confirmText={seedResult !== null ? t('admin.common.close') : t('admin.interests.seed')}
        cancelText={seedResult !== null ? undefined : t('admin.common.cancel')}
        variant="info"
        isLoading={isProcessing}
      />
    </div>
  );
}
